import TokenStore from "../../classes/TokenStore.js";
import Dialog from "../../classes/Dialog.js";
import Template from "../../classes/Template.js";
import InPlayCounter from "../../classes/InPlayCounter.js";
import {
    lookupOne,
    lookupOneCached,
    replaceContentsMany
} from "../../utils/elements.js";
import {
    emptyObject
} from "../../utils/objects.js";
import {
    gameObserver,
    tokenObserver
} from "../../constants/observers.js";

/**
 * Sets up a character-list dialog for the given teams (Travellers, Fabled,
 * Loric, ...). Handles populating the list with official and homebrew
 * characters, adding characters to the grimoire when clicked, counting how
 * many of each character are in play, and syncing reminder tokens.
 *
 * @param {Object} config
 *        Configuration for the process.
 * @param {Array.<String>} config.teams
 *        Team names whose characters belong in this list.
 * @param {String} config.listSelector
 *        Selector for the element that lists the characters.
 * @param {String} config.addButtonSelector
 *        Selector for the button that opens the dialog.
 * @param {String} config.dialogSelector
 *        Selector for the dialog element.
 * @param {Function} [config.onCharactersSelected]
 *        Optional callback executed after "characters-selected" has been
 *        handled. Receives the event detail.
 */
export default function createTeamListProcess({
    teams,
    listSelector,
    addButtonSelector,
    dialogSelector,
    onCharactersSelected
}) {

    const isTeam = (character) => teams.includes(character.getTeam());

    const official = Object.fromEntries(
        teams.map((team) => [team, Object.create(null)])
    );
    const homebrew = Object.fromEntries(
        teams.map((team) => [team, Object.create(null)])
    );
    const inPlay = new InPlayCounter(tokenObserver);
    let characterTemplate = null;

    function populate() {

        if (!characterTemplate) {

            characterTemplate = Template.create(
                lookupOneCached("#character-list-template")
            );

        }

        const contents = teams.flatMap((team) =>
            Object.values({ ...official[team], ...homebrew[team] })
                .sort((a, b) => a.getName().localeCompare(b.getName()))
                .map((character) => characterTemplate.draw({
                    ".js--character-list--item,.js--character-list--button"(element) {
                        element.dataset.tokenId = character.getId();
                    },
                    ".js--character-list--token"(element) {
                        element.append(character.drawToken());
                    }
                }))
        );

        replaceContentsMany(lookupOneCached(listSelector), contents);
        lookupOneCached(addButtonSelector).disabled = false;

    }

    gameObserver.on("characters-selected", ({ detail }) => {

        teams.forEach((team) => emptyObject(homebrew[team]));

        detail.characters
            .filter(isTeam)
            .forEach((character) => {
                homebrew[character.getTeam()][character.getId()] = character;
            });

        populate();
        onCharactersSelected?.(detail);

    });

    TokenStore.ready((tokenStore) => {

        teams.forEach((team) => emptyObject(official[team]));

        tokenStore
            .getAllCharacters()
            .filter(isTeam)
            .forEach((character) => {
                official[character.getTeam()][character.getId()] = character;
            });

        populate();

    });

    // Add to the grimoire when clicked.

    const pad = lookupOneCached(".js--pad").pad;

    lookupOneCached(listSelector).addEventListener("click", ({ target }) => {

        const button = target.closest("[data-token-id]");

        if (!button) {
            return;
        }

        TokenStore.ready((tokenStore) => {

            pad.addCharacter(tokenStore.getCharacter(button.dataset.tokenId));
            Dialog.create(lookupOneCached(dialogSelector)).hide();

        });

    });

    // Night Order.
    // #155 - Let the NightOrder class manage the night order.

    tokenObserver.on("character-add", ({ detail }) => {

        const character = detail.character;

        if (!isTeam(character)) {
            return;
        }

        character.getReminders().forEach((reminder) => {
            lookupOneCached("#reminder-list__list").append(reminder.drawList());
        });

    });

    tokenObserver.on("character-remove", ({ detail }) => {

        const character = detail.character;

        if (!isTeam(character) || inPlay.isInPlay(character.getId())) {
            return;
        }

        character.getReminders().forEach((reminder) => {

            lookupOne(
                `[data-reminder-id="${reminder.getId()}"]`,
                lookupOneCached("#reminder-list__list")
            )?.remove();

        });

    });

}
